// AdminBlog.jsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AdminLayout } from './AdminDashboard';
import { LuPlus, LuPencil, LuTrash2, LuEye, LuClock, LuImage, LuSearch, LuVideo } from 'react-icons/lu';
import TiptapEditor from '../../components/admin/TiptapEditor';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

export function AdminBlog() {
  const [showForm, setShowForm] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({ 
    title: '', 
    slug: '', 
    excerpt: '', 
    content: '', 
    category: 'travel', 
    isPublished: false, 
    metaTitle: '', 
    metaDescription: '',
    coverImage: { url: '', alt: '' },
    video: { url: '' },
    tags: ''
  });
  
  const qc = useQueryClient();

  const { data: posts, isLoading } = useQuery({ 
    queryKey: ['admin-blogs'], 
    queryFn: () => axios.get('/api/blog/admin/all').then(r => r.data).catch(() => []) 
  });

  const saveMutation = useMutation({
    mutationFn: (data) => {
      const payload = {
        ...data,
        tags: typeof data.tags === 'string' ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : data.tags
      };
      return editPost ? axios.put(`/api/blog/${editPost._id}`, payload) : axios.post('/api/blog', payload);
    },
    onSuccess: () => { 
      toast.success(editPost ? 'Post updated!' : 'Post created!'); 
      qc.invalidateQueries(['admin-blogs']); 
      setShowForm(false); 
      setEditPost(null); 
      resetForm();
    },
    onError: (err) => {
      const msg = err.response?.data?.message || 'Failed to save post. Try logging out and back in.';
      toast.error(msg);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/blog/${id}`),
    onSuccess: () => { 
      toast.success('Post deleted successfully'); 
      qc.invalidateQueries(['admin-blogs']); 
    }
  });

  const resetForm = () => {
    setForm({ 
      title: '', 
      slug: '', 
      excerpt: '', 
      content: '', 
      category: 'travel', 
      isPublished: false, 
      metaTitle: '', 
      metaDescription: '',
      coverImage: { url: '', alt: '' },
      video: { url: '' },
      tags: ''
    });
  };

  const handleEdit = (p) => { 
    setEditPost(p); 
    setForm({
      ...p,
      tags: p.tags?.join(', ') || '',
      coverImage: p.coverImage || { url: '', alt: '' },
      video: p.video || { url: '' }
    }); 
    setShowForm(true); 
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredPosts = (posts || []).filter(p => 
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet><title>Blog Management – Villa Tesori Admin</title></Helmet>
      <AdminLayout title="Blog Management">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', color: 'var(--forest)', marginBottom: 4 }}>Journal Entries</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Manage your travel stories and local guides</p>
          </div>
          {!showForm && (
            <button 
              className="btn btn-primary" 
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px' }} 
              onClick={() => { setShowForm(true); setEditPost(null); resetForm(); }}
            >
              <LuPlus size={18} /> New Adventure
            </button>
          )}
        </div>

        {showForm && (
          <div style={{ background: 'var(--white)', border: '1px solid #e8e8e8', borderRadius: 4, padding: 32, marginBottom: 40, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', color: 'var(--forest)' }}>
                {editPost ? 'Edit Story' : 'Write a New Story'}
              </h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.88rem' }}>Cancel</button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input 
                  className="form-control" 
                  placeholder="e.g. 7 Best Treks Near Villa Tesori"
                  value={form.title} 
                  onChange={e => {
                    const title = e.target.value;
                    const slug = title.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
                    setForm(p=>({...p, title, slug: editPost ? p.slug : slug}));
                  }} 
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Slug (URL) *</label>
                <input 
                  className="form-control" 
                  value={form.slug} 
                  onChange={e => setForm(p=>({...p, slug: e.target.value}))} 
                  required 
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="form-group">
                <label className="form-label">Cover Image</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div 
                    onClick={() => document.getElementById('cover-image-upload').click()}
                    style={{ 
                      width: 120, height: 80, border: '2px dashed #ddd', borderRadius: 4, 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      cursor: 'pointer', overflow: 'hidden', background: '#fafafa' 
                    }}
                  >
                    {form.coverImage?.url ? (
                      <img src={form.coverImage.url} alt="Cover Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <LuImage size={24} style={{ color: '#ccc' }} />
                    )}
                  </div>
                  <input 
                    id="cover-image-upload" type="file" accept="image/*" hidden 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const toastId = toast.loading('Uploading image...');
                        try {
                          const url = await uploadToCloudinary(file);
                          setForm(p=>({...p, coverImage: {...p.coverImage, url}}));
                          toast.success('Image uploaded!', { id: toastId });
                        } catch (err) {
                          toast.error(err.message || 'Upload failed');
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                  <div>
                    <button type="button" className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '0.78rem' }} onClick={() => document.getElementById('cover-image-upload').click()}>
                      Select Photo
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Featured Video (Optional)</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div 
                    onClick={() => document.getElementById('video-upload').click()}
                    style={{ 
                      width: 120, height: 80, border: '2px dashed #ddd', borderRadius: 4, 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      cursor: 'pointer', overflow: 'hidden', background: '#fafafa' 
                    }}
                  >
                    {form.video?.url ? (
                      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <video src={form.video.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                          <LuVideo size={20} />
                        </div>
                      </div>
                    ) : (
                      <LuVideo size={24} style={{ color: '#ccc' }} />
                    )}
                  </div>
                  <input 
                    id="video-upload" type="file" accept="video/*" hidden 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const toastId = toast.loading('Uploading video...');
                        try {
                          const url = await uploadToCloudinary(file);
                          setForm(p=>({...p, video: { url }}));
                          toast.success('Video uploaded!', { id: toastId });
                        } catch (err) {
                          toast.error(err.message || 'Video upload failed');
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                  <div>
                    <button type="button" className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '0.78rem' }} onClick={() => document.getElementById('video-upload').click()}>
                      Select Video
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" value={form.category} onChange={e => setForm(p=>({...p, category: e.target.value}))}>
                  {['travel','nature','food','adventure','culture','tips'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tags (comma separated)</label>
                <input 
                  className="form-control" 
                  placeholder="trekking, wayanad, adventure"
                  value={form.tags} 
                  onChange={e => setForm(p=>({...p, tags: e.target.value}))} 
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Excerpt (Short Hook)</label>
              <textarea 
                className="form-control" 
                value={form.excerpt} 
                onChange={e => setForm(p=>({...p, excerpt: e.target.value}))} 
                rows={2} 
              />
            </div>

            <div className="form-group">
              <label className="form-label">Story Content (Rich Text Editor)</label>
              <TiptapEditor 
                key={editPost?._id || 'new'}
                content={form.content} 
                onChange={html => setForm(p => ({ ...p, content: html }))} 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div className="form-group">
                <label className="form-label">SEO Meta Title</label>
                <input 
                  className="form-control" 
                  value={form.metaTitle} 
                  onChange={e => setForm(p=>({...p, metaTitle: e.target.value}))} 
                />
              </div>
              <div className="form-group">
                <label className="form-label">SEO Meta Description</label>
                <textarea 
                  className="form-control" 
                  value={form.metaDescription} 
                  onChange={e => setForm(p=>({...p, metaDescription: e.target.value}))} 
                  rows={2}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: 'var(--forest)' }}>
                <input 
                  type="checkbox" 
                  checked={form.isPublished} 
                  onChange={e => setForm(p=>({...p, isPublished: e.target.checked}))}
                  style={{ width: 18, height: 18, accentColor: 'var(--forest)' }}
                />
                Published
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  className="btn btn-primary" 
                  style={{ padding: '12px 32px' }} 
                  onClick={() => saveMutation.mutate(form)}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save Adventure'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!showForm && (
          <div style={{ background: 'var(--white)', borderRadius: 4, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ position: 'relative', width: 300 }}>
                <LuSearch size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input 
                  type="text" 
                  placeholder="Search articles..." 
                  style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.82rem' }}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Showing {filteredPosts.length} posts</div>
            </div>

            {isLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading journal entries...</div>
            ) : filteredPosts.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>No entries found.</p>
                <button className="btn btn-outline" onClick={() => setSearchTerm('')}>Clear Search</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                {filteredPosts.map((post) => (
                  <div key={post._id} style={{ padding: '20px 24px', borderBottom: '1px solid #f5f5f5', display: 'flex', gap: 24, alignItems: 'center', transition: 'background 0.2s' }}>
                    <div style={{ width: 80, height: 60, borderRadius: 4, background: '#f5f5f5', overflow: 'hidden', flexShrink: 0 }}>
                      {post.coverImage?.url ? (
                        <img src={post.coverImage.url} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                          <LuImage size={24} />
                        </div>
                      )}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                        <h4 style={{ fontWeight: 600, color: 'var(--forest)', fontSize: '1rem' }}>{post.title}</h4>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: 4, 
                          fontSize: '0.68rem', 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.05em',
                          background: post.isPublished ? '#e8f4e8' : '#fff0f0', 
                          color: post.isPublished ? '#2d5a2d' : '#a00' 
                        }}>
                          {post.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><LuClock size={12} /> {post.readTime} min</span>
                        <span style={{ textTransform: 'capitalize' }}>Category: {post.category}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><LuEye size={12} /> {post.views} views</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button 
                        onClick={() => handleEdit(post)} 
                        style={{ padding: '8px', borderRadius: 4, border: '1px solid #eee', background: '#fff', cursor: 'pointer', color: 'var(--forest)' }}
                        title="Edit Post"
                      >
                        <LuPencil size={16} />
                      </button>
                      <button 
                        onClick={() => {
                          if (window.confirm('Delete this story?')) {
                            deleteMutation.mutate(post._id);
                          }
                        }} 
                        style={{ padding: '8px', borderRadius: 4, border: '1px solid #ffebeb', background: '#fff', cursor: 'pointer', color: '#c00' }}
                        title="Delete Post"
                      >
                        <LuTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </AdminLayout>
    </>
  );
}
export default AdminBlog;
